// Analytics tab › Smart Pages: master-detail — Smart Pages (S) on the left
// (with used/unused badges from the object catalog), the EXISTING embedded
// PageDesignerPage on the right. "New" either generates a page with AI or
// creates a blank one; pages publish to the app menu like dashboards do.
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { ListPlus, Lock, Globe, FileText, CheckCircle2, Circle } from 'lucide-react'
import PageDesignerPage from '@/pages/designers/PageDesignerPage'
import PagePreviewModal from '@/pages/designers/page/PreviewModal'
import {
  generatePageObject,
  savePageObject,
  deletePageObject,
  listProjectPages,
  objectCatalog,
  type CatalogObject,
  type ProjectPageSummary,
} from '@/api/designers'
import { makeBlock } from '@/pages/designers/page/blocks'
import type { PageModel } from '@/pages/designers/page/types'
import ObjectMasterDetail, { type MasterItem } from './ObjectMasterDetail'
import PreviewThumbnail, { bustPreview } from './PreviewThumbnail'
import PublishToMenuDialog, { type PublishTarget } from './PublishToMenuDialog'
import { EmptyStateCoach, TypeChip, errMessage, slugify } from './shared'

// Same seed the page designer uses for a brand-new page (title intentionally
// blank — the author opts in by typing one in Page Properties).
function blankPageModel(objName: string, description: string): PageModel {
  return {
    obj_name: objName,
    title: '',
    description,
    inputs: [],
    blocks: [makeBlock('paragraph', 'paragraph_1')],
    kind: 'internal',
  }
}

// Page kind → the small icon shown against the title (with tooltip). Internal
// pages are login-gated (lock), external are public site pages (globe), and
// document templates spawn document instances (file).
function kindIcon(kind: string): { node: React.ReactNode; label: string } {
  if (kind === 'external') {
    return { node: <Globe size={15} />, label: 'Public site page' }
  }
  if (kind === 'document') {
    return { node: <FileText size={15} />, label: 'Document template' }
  }
  return { node: <Lock size={15} />, label: 'Internal (login-gated) page' }
}

export default function SmartPagesSection({
  project, registerLeaveGuard, initialObjName, openNonce, onChanged,
}: {
  project: string
  registerLeaveGuard?: (fn: ((proceed: () => void) => void) | null) => void
  initialObjName?: string
  openNonce?: number
  onChanged?: () => void
}) {
  const [pages, setPages] = useState<ProjectPageSummary[]>([])
  const [catalog, setCatalog] = useState<Record<string, CatalogObject>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [menuTarget, setMenuTarget] = useState<PublishTarget | null>(null)
  // "View" from a card → the page designer's full-screen preview modal (loads
  // the SAVED page from disk and renders it with the real runtime).
  const [viewObj, setViewObj] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // The list gives rich meta; the catalog adds used/unused. The catalog
      // is best-effort — the list alone is enough to render.
      const [listRes, catRes] = await Promise.allSettled([
        listProjectPages(project),
        objectCatalog(project, 'S'),
      ])
      if (listRes.status === 'rejected') throw listRes.reason
      setPages(listRes.value.pages || [])
      if (catRes.status === 'fulfilled') {
        const map: Record<string, CatalogObject> = {}
        for (const o of catRes.value.objects || []) map[o.obj_name] = o
        setCatalog(map)
      }
    } catch (err) {
      setError(errMessage(err, 'Could not load smart pages'))
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void refresh() }, [refresh])

  const items: MasterItem[] = pages.map((p) => {
    const cat = catalog[p.obj_name]
    const hasTitle = !!p.title && p.title !== p.obj_name
    return {
      id: p.obj_name,
      name: p.title || p.obj_name,
      // Only show the mono obj_name line when there's a distinct title above
      // it — otherwise the name IS the obj_name and it would read twice.
      subtitle: hasTitle ? p.obj_name : undefined,
      description: p.description || undefined,
      // Kind moved to the title icon; keep only a compact input-count pill.
      tags: p.input_count > 0
        ? [`${p.input_count} input${p.input_count === 1 ? '' : 's'}`]
        : undefined,
      titleIcon: kindIcon(p.kind || 'internal'),
      badge: cat
        ? (cat.used
            ? { text: 'On the app menu / in use', tone: 'success' as const, icon: <CheckCircle2 size={15} style={{ color: 'var(--color-success)' }} /> }
            : { text: 'Not used yet', tone: 'muted' as const, icon: <Circle size={14} style={{ color: 'var(--color-text-faint)' }} /> })
        : undefined,
    }
  })

  const handleCreate = async (name: string, description: string): Promise<string | null> => {
    const objName = slugify(name)
    try {
      await generatePageObject({
        project,
        obj_name: objName,
        description: description || name,
      })
      toast.success(`Smart page "${name}" drafted — open it to fine-tune the blocks`)
      await refresh()
      onChanged?.()
      return objName
    } catch (err) {
      toast.error(errMessage(err, 'Could not generate the smart page'))
      return null
    }
  }

  // Manual path: persist a blank page (no AI) and open the designer so the
  // user builds the blocks themselves.
  const handleCreateManual = async (name: string, description: string): Promise<string | null> => {
    const objName = slugify(name)
    try {
      await savePageObject(project, objName, { page: blankPageModel(objName, description) })
      toast.success(`Smart page "${name}" created — add blocks in the designer`)
      await refresh()
      onChanged?.()
      return objName
    } catch (err) {
      toast.error(errMessage(err, 'Could not create the smart page'))
      return null
    }
  }

  return (
    <>
    <ObjectMasterDetail
      project={project}
      typeCode="S"
      heading="Smart Pages"
      subCopy="Notion-style pages that embed visuals, dashboards, forms and text. Build one from scratch or draft it with AI, then add it to the application menu like any visual."
      items={items}
      loading={loading}
      error={error}
      onRetry={() => void refresh()}
      newLabel="New smart page"
      newDialogTitle="New smart page"
      namePlaceholder="e.g. Sales home"
      descPlaceholder="e.g. A landing page with a KPI headline, the sales dashboard, and quick links to key transactions."
      createButtonLabel="Generate with AI"
      onCreate={handleCreate}
      onCreateManual={handleCreateManual}
      manualButtonLabel="Create manually"
      registerLeaveGuard={registerLeaveGuard}
      initialObjName={initialObjName}
      openNonce={openNonce}
      renderPreview={(item) => (
        <PreviewThumbnail project={project} objName={item.id} kind="S" />
      )}
      onEditorClose={(obj) => bustPreview(project, 'S', obj)}
      onView={(obj) => setViewObj(obj)}
      deleteConfig={{
        objType: 'S',
        objLabel: 'smart page',
        doDelete: async (id) => { await deletePageObject(project, id) },
        onDeleted: () => { void refresh(); onChanged?.() },
      }}
      detailToolbar={(objName) => {
        const cat = catalog[objName]
        if (!cat || cat.in_navigation) return null
        const p = pages.find((x) => x.obj_name === objName)
        return (
          <button
            className="btn btn-secondary btn-sm"
            title="Wire this smart page into the application menu — no regeneration"
            onClick={() => setMenuTarget({
              obj_name: objName,
              obj_type: 'S',
              title: p?.title || cat.title || objName,
            })}
          >
            <ListPlus size={13} />
            Add to menu…
          </button>
        )
      }}
      emptyState={(openNew) => (
        <EmptyStateCoach
          icon={<TypeChip code="S" size={26} />}
          title="No smart pages yet"
          body="A smart page is a block-based page that can embed visuals, dashboards, forms and rich text. Create a blank one and build it yourself, or draft it from a description with AI."
          action={
            <button className="btn btn-primary btn-sm" onClick={openNew}>
              + New smart page
            </button>
          }
        />
      )}
      renderDesigner={(objName, common) => (
        <PageDesignerPage key={`${project}:${objName}`} {...common} />
      )}
    />
    <PublishToMenuDialog
      open={menuTarget !== null}
      mode="menu"
      project={project}
      targets={menuTarget ? [menuTarget] : []}
      onClose={() => setMenuTarget(null)}
      onPublished={() => { void refresh(); onChanged?.() }}
    />
    <PagePreviewModal
      open={viewObj !== null}
      projectName={project}
      objectName={viewObj || ''}
      onClose={() => setViewObj(null)}
    />
    </>
  )
}
